<template>
  <q-dialog v-model="showDialog" persistent>
    <q-card class="otp-verification-card">
      <q-card-section class="text-center q-pb-md q-pt-lg">
        <div v-if="!persistent" style="position: absolute; left: 16px; top: 16px;">
          <q-btn icon="close" flat round dense @click="onCancel" />
        </div>
        
        <q-icon name="lock_open" size="64px" color="primary" class="q-mb-md" />
        
        <div class="text-h5 text-weight-medium q-mb-sm">کد تأیید</div>
        <p class="text-body2" style="line-height: 1.6; font-weight: 400;">
          کد تأیید ۶ رقمی به شماره <strong>{{ phoneNumber }}</strong> ارسال شد.
        </p>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <q-form @submit="onSubmit">
          <q-input
            v-model="otpCode"
            filled
            label="کد تأیید"
            :maxlength="6"
            inputmode="numeric"
            :rules="[
              val => !!val || 'کد تأیید الزامی است',
              val => {
                const englishVal = persianToEnglish ? persianToEnglish(val) : val;
                return /^\d{6}$/.test(englishVal) || 'کد تأیید باید ۶ رقم باشد';
              }
            ]"
            lazy-rules
            :disable="loading"
            class="modern-input q-mb-md"
            style="direction: ltr !important;"
            hide-bottom-space
            no-error-icon
            autofocus
            @keypress="onlyNumbers"
          />

          <div v-if="timeToResend > 0" class="text-center q-mb-md text-caption text-grey-6">
            امکان ارسال مجدد در {{ timeToResend }} ثانیه.
          </div>
          <q-btn
            v-else
            flat
            dense
            no-caps
            class="full-width q-mb-md text-primary"
            :loading="resending"
            @click="resendOTP"
          >
            ارسال مجدد کد تأیید
          </q-btn>

          <div class="button-container">
            <q-btn
              v-if="!persistent"
              label="بازگشت"
              flat
              @click="onBack"
              :disable="loading"
              class="back-btn"
            />
            <q-btn
              label="تأیید"
              type="submit"
              unelevated
              :loading="loading"
              :disable="loading"
              class="modern-button submit-btn"
            />
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance } from 'vue';
import { useAuthStore } from 'src/stores/auth';
import { useQuasar } from 'quasar';

const appContext = getCurrentInstance();
const persianToEnglish = appContext?.appContext.config.globalProperties.$persianToEnglish;

interface Props {
  modelValue: boolean;
  phoneNumber: string;
  persistent?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'verified'): void;
  (e: 'back'): void;
  (e: 'cancel'): void;
}

const props = withDefaults(defineProps<Props>(), {
  persistent: false
});

const emit = defineEmits<Emits>();

const authStore = useAuthStore();
const $q = useQuasar();

const showDialog = ref(props.modelValue);
const otpCode = ref('');
const loading = ref(false);
const resending = ref(false);
const timeToResend = ref(0);
let resendInterval: ReturnType<typeof setInterval> | null = null;

// Watch for external changes to modelValue
watch(() => props.modelValue, (newVal) => {
  showDialog.value = newVal;
  if (newVal) {
    // Reset state when dialog opens
    otpCode.value = '';
    startResendCountdown();
  } else {
    // Clean up countdown when dialog closes
    if (resendInterval) {
      clearInterval(resendInterval);
      resendInterval = null;
    }
  }
});

// Watch for internal changes to showDialog
watch(showDialog, (newVal) => {
  emit('update:modelValue', newVal);
});

function onlyNumbers(event: KeyboardEvent) {
  const key = event.key;
  // Allow both English (0-9) and Persian (۰-۹) numbers
  if (!key.match(/[0-9۰-۹]/)) {
    event.preventDefault();
  }
}

async function onSubmit() {
  // Convert Persian digits to English
  const englishOtpCode = persianToEnglish ? persianToEnglish(otpCode.value) : otpCode.value;
  
  if (!englishOtpCode || !/^\d{6}$/.test(englishOtpCode)) {
    $q.notify({
      type: 'negative',
      message: 'لطفاً کد تأیید ۶ رقمی را وارد کنید',
      position: 'top'
    });
    return;
  }

  loading.value = true;

  try {
    const response = await authStore.$api.post('verify_phone_and_merge', {
      auth_code: englishOtpCode
    });

    if (response.data.status === 'ok') {
      await authStore.setTokens({
        access: response.data.access_token,
        refresh: response.data.refresh_token
      });
      
      // Refresh profile to get updated user data
      try {
        await authStore.fetchProfile();
      } catch (err) {
        console.error('Error refreshing profile after merge:', err);
      }
      
      // Close dialog
      emit('verified');
      showDialog.value = false;
    } else {
      $q.notify({
        type: 'negative',
        message: response.data.message || 'خطایی رخ داده است',
        position: 'top'
      });
    }
  } catch (error: any) {
    console.error('OTP verification error:', error);
    const message = error.response?.status === 429 
      ? 'تعداد تلاش‌های شما بیش از حد مجاز است. لطفاً بعداً تلاش کنید.'
      : error.response?.data?.message || 'کد تأیید اشتباه است';
    
    $q.notify({
      type: 'negative',
      message,
      position: 'top'
    });
  } finally {
    loading.value = false;
  }
}

function onBack() {
  emit('back');
  showDialog.value = false;
}

function onCancel() {
  if (!props.persistent) {
    emit('cancel');
    showDialog.value = false;
  }
}

function startResendCountdown() {
  timeToResend.value = 60; // 60 seconds countdown
  if (resendInterval) {
    clearInterval(resendInterval);
  }
  resendInterval = setInterval(() => {
    timeToResend.value--;
    if (timeToResend.value <= 0 && resendInterval) {
      clearInterval(resendInterval);
      resendInterval = null;
    }
  }, 1000);
}

async function resendOTP() {
  if (timeToResend.value > 0 || resending.value) return;
  
  resending.value = true;
  
  try {
    // Convert Persian digits to English
    const englishPhoneNumber = persianToEnglish ? persianToEnglish(props.phoneNumber) : props.phoneNumber;
    
    const response = await authStore.$api.post('initiate_phone_verification', {
      phone_number: englishPhoneNumber,
      is_app: false
    });
    
    if (response.data.status === 'ok') {
      $q.notify({
        type: 'positive',
        message: 'کد تأیید مجدداً ارسال شد',
        position: 'top'
      });
      startResendCountdown();
    } else {
      $q.notify({
        type: 'negative',
        message: response.data.message || 'خطا در ارسال مجدد کد',
        position: 'top'
      });
    }
  } catch (error: any) {
    console.error('Resend OTP error:', error);
    $q.notify({
      type: 'negative',
      message: error.response?.data?.message || 'خطا در ارسال مجدد کد',
      position: 'top'
    });
  } finally {
    resending.value = false;
  }
}
</script>

<script lang="ts">
import { watch } from 'vue';
export default {
  name: 'OTPVerificationDialog'
};
</script>

<style lang="scss" scoped>
.otp-verification-card {
  min-width: 350px;
  max-width: 450px;
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.modern-input {
  :deep(.q-field__control) {
    border-radius: 12px;
    height: 56px;

    &:before {
      border: none;
    }

    &:after {
      display: none;
    }
  }

  :deep(.q-field__native) {
    font-size: 1.1em;
    padding-right: 12px;
    line-height: normal;
    padding-top: 0;
    padding-bottom: 0;
    margin-top: auto;
    margin-bottom: auto;
  }

  :deep(.q-field__label) {
    top: 18px;
  }

  &:deep(.q-field--float .q-field__label) {
    display: none;
  }
}

.modern-button {
  border-radius: 12px;
  height: 56px;
  font-size: 1.1em;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-weight: 500;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
  }
}

.button-container {
  display: flex;
  gap: 12px;
  
  .back-btn {
    border-radius: 12px;
    height: 56px;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }
  
  .submit-btn {
    flex: 1;
    color: var(--inverted-text);
    background-color: var(--inverted-main);
  }
}
</style>
